import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Camera, RefreshCcw, Image as ImageIcon, Zap, Layers, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function CapturePage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState('user');
  const [active, setActive] = useState(false);
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const start = async (mode = facing) => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setActive(true);
    } catch (e) {
      toast.error('Camera not available. Use Upload instead.');
      setActive(false);
    }
  };

  useEffect(() => { start(facing); return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); }; }, []); // eslint-disable-line

  const flip = async () => { const next = facing === 'user' ? 'environment' : 'user'; setFacing(next); await start(next); };

  const snap = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current; const c = canvasRef.current;
    c.width = v.videoWidth || 720; c.height = v.videoHeight || 1280;
    const ctx = c.getContext('2d'); ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => { if (b) setShot(b); }, 'image/jpeg', 0.92);
  };

  const onPick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setShot(f);
  };

  const useShot = async () => {
    if (!shot) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', shot, shot.name || 'capture.jpg');
      const { data } = await api.post('/files/upload', fd);
      nav('/post/new', { state: { mediaId: data.id, contentType: data.content_type } });
    } catch (e) { toast.error('Upload failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative h-[calc(100dvh-56px)] w-full overflow-hidden bg-black md:h-[calc(100dvh-56px)]" data-testid="capture-screen">
      <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
      <canvas ref={canvasRef} className="hidden" />
      {/* haze top */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/65" />

      <div className="absolute left-0 right-0 top-0 flex items-center justify-between p-4">
        <Button variant="ghost" size="sm" className="glass rounded-full border" onClick={flip} data-testid="capture-flip-camera-button">
          <RefreshCcw className="mr-1 h-4 w-4" /> Flip
        </Button>
        <span className="feed-accent-pill">Capture</span>
        <Button variant="ghost" size="sm" className="glass rounded-full border" onClick={() => fileRef.current?.click()} data-testid="capture-upload-button">
          <ImageIcon className="mr-1 h-4 w-4" /> Upload
        </Button>
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onPick} className="hidden" data-testid="capture-file-input" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 pb-24 md:pb-6">
        <div className="mx-auto flex max-w-md items-center justify-between gap-4">
          <Button variant="ghost" className="glass rounded-full border" onClick={() => nav('/post/new')} data-testid="capture-skip-to-composer">
            <Plus className="mr-1 h-4 w-4" /> Skip
          </Button>
          <button onClick={snap} disabled={!active} className="feed-accent-border grid h-20 w-20 place-items-center rounded-full border-4 bg-black/40 backdrop-blur disabled:opacity-50" data-testid="capture-shutter-button">
            <span className="h-14 w-14 rounded-full bg-white shadow-[var(--shadow-elev-2)]" />
          </button>
          <Button variant="ghost" className="glass rounded-full border" onClick={() => nav('/feed')} data-testid="capture-go-feed">
            <Layers className="mr-1 h-4 w-4" /> Feed
          </Button>
        </div>
        <div className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">photo · swipe to compose</div>
      </div>

      {shot && (
        <div className="absolute inset-0 z-50 grid place-items-center bg-black/80 p-4">
          <div className="w-full max-w-md rounded-[var(--radius)] border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-3">
            {(shot.type || '').startsWith('video/')
              ? <video src={URL.createObjectURL(shot)} controls className="h-72 w-full rounded-lg object-cover" />
              : <img src={URL.createObjectURL(shot)} alt="" className="h-72 w-full rounded-lg object-cover" />}
            <div className="mt-3 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setShot(null)}>Retake</Button>
              <Button onClick={useShot} disabled={busy} data-testid="capture-use-shot"><Zap className="mr-1 h-4 w-4" /> {busy ? 'Uploading…' : 'Use'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
